import React, { Component } from 'react';

import PropTypes from 'prop-types';

import { BasicButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-helpers';
import Icon from '@adsk/alloy-react-icon';
import theme, { getInteractiveStyles } from '@adsk/alloy-react-theme';

import {
  defaultValueRenderer,
  defaultValueTagRenderer,
} from './defaultRenderers';

/**
 * SelectBoxControl
 */
class SelectBoxControl extends Component {
  constructor(props) {
    super(props);

    this.handleControlClick = this.handleControlClick.bind(this);
    this.handleSearchClear = this.handleSearchClear.bind(this);
    this.handleMenuToggle = this.handleMenuToggle.bind(this);
    this.getValueTagProps = this.getValueTagProps.bind(this);
  }

  handleControlClick(e) {
    e.stopPropagation();

    const { disabled, searchable, stateAndHelpers } = this.props;
    const { isOpen, openMenu, closeMenu } = stateAndHelpers;

    if (disabled) return;
    if (!searchable) {
      isOpen ? closeMenu() : openMenu();
    } else if (!isOpen) {
      openMenu();
    }
  }

  handleMouseDown(e) {
    e.preventDefault();
  }

  handleSearchClear() {
    const { stateAndHelpers, onInputChange } = this.props;
    stateAndHelpers.setState({ inputValue: '' }, () => {
      onInputChange('', stateAndHelpers);
    });
  }

  handleMenuToggle(e) {
    const { isOpen, closeMenu } = this.props.stateAndHelpers;
    if (isOpen) {
      e.preventDefault();
      closeMenu();
    }
  }

  handleValueTagRemove(item) {
    const { stateAndHelpers, multiple, selectedValue, valueKey } = this.props;
    const { isOpen, inputValue } = stateAndHelpers;
    if (multiple) {
      stateAndHelpers.selectItem(
        selectedValue.filter((x) => x[valueKey] !== item[valueKey]),
        {
          isOpen,
          inputValue,
        }
      );
    } else if (selectedValue && selectedValue[valueKey] === item[valueKey]) {
      stateAndHelpers.clearSelection();
    }
  }

  getValueTagProps({ item, ...rest }) {
    const eventHandlers = {
      onMouseDown: (e) => {
        e.preventDefault();
      },
      onClick: (e) => {
        e.stopPropagation();
        this.handleValueTagRemove(item);
      },
    };

    return { ...eventHandlers, ...rest };
  }

  renderPlaceholder() {
    const { isOpen } = this.props.stateAndHelpers;
    const {
      searchable,
      placeholder = this.props['T'].translate('SELECT'),
      searchPlaceholder = this.props['T'].translate('SEARCH'),
    } = this.props;
    const inputPlaceholder =
      isOpen && searchable ? searchPlaceholder : placeholder;

    return (
      <div
        css={[
          theme.typography.labelMedium,
          {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: theme.colors.charcoal500,
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            userSelect: 'none',
          },
        ]}
      >
        {inputPlaceholder}
      </div>
    );
  }

  renderInput() {
    const {
      stateAndHelpers,
      autoFocus,
      searchable,
      multiple,
      selectedValue,
      showValueAsTags,
    } = this.props;
    const { getInputProps, inputValue } = stateAndHelpers;

    return (
      <input
        data-testid="SelectBox__input"
        dir={'auto'}
        css={{
          backgroundColor: 'transparent',
          border: 'none',
          outline: 'none',
          padding: 0,
          textAlign: 'left',
          flex: 'auto',

          ['&::-ms-clear']: {
            display: 'none',
          },
        }}
        {...getInputProps({
          autoFocus: searchable && autoFocus,
          autoComplete: 'off',
          onKeyDown: (event) => {
            if (
              event.key === 'Backspace' &&
              searchable &&
              showValueAsTags &&
              !inputValue
            ) {
              if (multiple && selectedValue.length > 0) {
                this.handleValueTagRemove(
                  selectedValue[selectedValue.length - 1]
                );
              } else if (!multiple && selectedValue) {
                this.handleValueTagRemove(selectedValue);
              }
            }
          },
        })}
      />
    );
  }

  renderValue() {
    const {
      stateAndHelpers,
      isSelected,
      selectedValue,
      searchable,
      valueRenderer = defaultValueRenderer,
    } = this.props;
    const { isOpen } = stateAndHelpers;

    if (!isSelected || (searchable && isOpen)) return null;
    return valueRenderer(selectedValue, this.props);
  }

  renderValueTags() {
    const {
      stateAndHelpers,
      selectedValue,
      container,
      disabled,
      searchable,
      multiple,
      valueTagRenderer = defaultValueTagRenderer,
    } = this.props;
    const { isOpen } = stateAndHelpers;

    const _stateAndHelpers = {
      ...stateAndHelpers,
      getValueTagProps: this.getValueTagProps,
    };
    const _props = {
      ...this.props,
      stateAndHelpers: _stateAndHelpers,
      container: container || this,
    };

    return (
      <div
        css={{
          display: 'flex',
          flexWrap: 'wrap',
          width: '100%',
          height: '100%',
        }}
      >
        {multiple &&
          selectedValue.map((item) => valueTagRenderer(item, _props))}
        {!multiple &&
          selectedValue &&
          !(searchable && isOpen) &&
          valueTagRenderer(selectedValue, _props)}
        {!disabled && searchable && isOpen && this.renderInput()}
      </div>
    );
  }

  renderToggle() {
    const { disabled, searchable, multiple, stateAndHelpers } = this.props;
    const { isOpen, inputValue } = stateAndHelpers;

    if (isOpen && searchable && multiple && !!inputValue) {
      return (
        <BasicButton
          data-testid="SelectBox__toggle"
          onMouseDown={this.handleMouseDown}
          onClick={this.handleSearchClear}
          style={{
            color: theme.colors.charcoal900,
            padding: '0 6',
            border: 0,

            '&:hover': {
              color: theme.colors.adskBlue700,
            },
          }}
        >
          <Icon
            size={20}
            type={Icon.TYPES.X}
            style={
              isOpen && {
                marginRight: 2,
                transform: 'rotate(180deg)',
              }
            }
          />
        </BasicButton>
      );
    }
    return (
      <BasicButton
        data-testid="SelectBox__toggle"
        style={{ color: '#DAE1E6' }}
        onClick={!disabled && searchable ? this.handleMenuToggle : undefined}
      >
        <Icon
          size={24}
          type={Icon.TYPES.CHEVRON_DOWN}
          style={{
            ...(isOpen && {
              transform: 'rotate(180deg)',
            }),

            ...(disabled
              ? {
                  color: theme.colors.charcoal500,
                }
              : {
                  color: theme.colors.charcoal900,
                }),
          }}
        />
      </BasicButton>
    );
  }

  render() {
    const {
      className,
      style,
      stateAndHelpers,
      isSelected,
      disabled,
      invalid,
      searchable,
      multiple,
      showValueAsTags,
      showSearchIcon,
      innerRef,
    } = this.props;
    const { getLabelProps, isOpen, inputValue } = stateAndHelpers;

    const showSearch =
      searchable &&
      showSearchIcon &&
      !(!isOpen && isSelected) &&
      !showValueAsTags;
    const showPlaceHolder =
      (!isSelected && !inputValue) ||
      (isSelected &&
        !inputValue &&
        isOpen &&
        searchable &&
        !(showValueAsTags && multiple));
    const showInput = !disabled && !showValueAsTags && searchable && isOpen;

    return (
      <label
        className={className}
        ref={innerRef}
        data-testid="SelectBox__control"
        css={[
          theme.typography.labelMedium,
          {
            position: 'relative',
            display: 'flex',
            cursor: 'pointer',
            backgroundColor: theme.colors.white,
            borderColor: theme.colors.charcoal400,
            borderRadius: 2,
            borderStyle: 'solid',
            borderWidth: 1,
            minHeight: 36,
            margin: 0,
            padding: 0,

            ...getInteractiveStyles(disabled),

            ...(isOpen && {
              borderColor: theme.colors.adskBlue500,
              boxShadow: `0 0 0 2px ${theme.colors.focusHalo}`,

              '&:hover': {
                boxShadow: `0 0 0 2px ${theme.colors.focusHalo}`,
              },
            }),

            ...(disabled && {
              backgroundColor: theme.colors.charcoal050,
              borderColor: theme.colors.charcoal200,
              cursor: 'default',
              pointerEvents: 'none',
            }),

            ...(invalid &&
              !disabled && {
                borderColor: theme.colors.red500,
              }),
          },
          style,
        ]}
        onClick={this.handleControlClick}
        {...getLabelProps()}
      >
        {showSearch && (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              color: '#819099',
              padding: '1px 0 0 5px',
            }}
          >
            <Icon type={Icon.TYPES.MAGNIFYING_GLASS} size={15} />
          </div>
        )}
        <div
          css={{
            position: 'relative',
            width: '100%',
            marginLeft: 16,
            padding: '3px 0',
            overflowX: 'hidden',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {showPlaceHolder && this.renderPlaceholder()}
          {showInput && this.renderInput()}
          {showValueAsTags ? this.renderValueTags() : this.renderValue()}
        </div>
        {this.renderToggle()}
      </label>
    );
  }
}

SelectBoxControl.displayName = 'SelectBoxControl';

SelectBoxControl.propTypes = {
  /**
   * Class name for the component
   */
  className: PropTypes.string,
  /**
   * style for the component
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * i18n helper for T.translate('KEY')
   */
  T: PropTypes.object,
  /**
   * Whether the component is disabled
   */
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * Whether the component is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether the component is invalid
   */
  invalid: PropTypes.bool,
  /**
   * Whether the component is searchable
   */
  searchable: PropTypes.bool,
  /**
   * stateAndHelpers pass to control from select
   */
  stateAndHelpers: PropTypes.shape({
    isOpen: PropTypes.bool,
    openMenu: PropTypes.func,
    closeMenu: PropTypes.func,
    selectItem: PropTypes.func,
    setState: PropTypes.func,
    clearSelection: PropTypes.func,
    getLabelProps: PropTypes.func,
    getInputProps: PropTypes.func,
    type: PropTypes.string,
    inputValue: PropTypes.string,
  }),
  /**
   * `(inputValue, stateAndHelpers) => *`, callback when searching
   */
  onInputChange: PropTypes.func,
  /**
   * Whether the component is multi selectable
   */
  multiple: PropTypes.bool,
  /**
   * selectedValue
   */
  selectedValue: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  /**
   * The key of the option value
   */
  valueKey: PropTypes.string,
  /**
   * The placeholder for the component
   */
  placeholder: PropTypes.node,
  /**
   * The placeholder for the component when searching
   */
  searchPlaceholder: PropTypes.node,
  /**
   * Whether the component is focused on mount
   */
  autoFocus: PropTypes.bool,
  /**
   * Whether the item is Selected
   */
  isSelected: PropTypes.bool,
  /**
   * func called on render value
   */
  valueRenderer: PropTypes.func,
  /**
   * Container for the menu, the menu would be rendered on an overlay if set
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * `(item, selectProps) => node`, custom value tag renderer
   */
  valueTagRenderer: PropTypes.func,
  /**
   * Whether to show value as tags
   */
  showValueAsTags: PropTypes.bool,
  /**
   * Whether to show search icon
   */
  showSearchIcon: PropTypes.bool,
};

const WrapperSelectBoxControl = (props) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  return <SelectBoxControl {...props} T={T} />;
};

export default WrapperSelectBoxControl;
